import { autoinject, bindable, Container } from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';
import { PubSub } from 'lib/event/PubSub';
import { AsyncErrorHandler, ComponentLoadingFader } from 'lib/ui';
import { MooringBridle, MooringComponentType, MooringReviewDto, UpdateMooringBridleDto } from 'models/mooring';
import { ToastService } from 'services';
import { MooringOfferService, MooringService } from 'services/mooring';
import { saveAs } from 'file-saver';
import { QueryFilter } from 'lib/tables/DataFilter';
import { OfferViewContext } from '../../OfferViewContext';
import { ConfirmDialog } from 'components/dialogs/confirm/confirm-dialog';

@autoinject
export class BridlesContainer {
  @bindable
  protected mooringId: number;

  @bindable
  protected reviewId?: number;
  protected review: MooringReviewDto;

  protected context = OfferViewContext.useContext(this.container);

  protected bridles: MooringBridle[] = [];

  constructor(
    private container: Container,
    protected element: Element,
    private mooringService: MooringService,
    private toaster: ToastService,
    private t: I18N,
    private pubsub: PubSub,
    private confirmation: ConfirmDialog,
    private mooringOfferService: MooringOfferService
  ) {
    pubsub.sub('filter-manager:filter-changed', (params: QueryFilter) => {
      void this.getBridles(params);
    });
    pubsub.sub('entity:updated', async () => {
      void this.getBridles();
    });
    pubsub.sub('list-entity:updated', async () => {
      void this.getBridles();
    });
  }

  protected controlView: boolean;
  protected toggleControlView() {
    this.controlView = !this.controlView;
  }

  @AsyncErrorHandler
  protected async exportBridles() {
    const blob = await this.mooringService.exportProductionBridles(this.mooringId);
    saveAs(blob.blob, blob.fileName);
  }

  @AsyncErrorHandler
  @ComponentLoadingFader
  async attached() {
    if (!this.mooringId) {
      throw new Error('mooringId is required => mooring-id.bind="<id>"');
    }

    await this.getBridles();
  }

  @AsyncErrorHandler
  protected async getBridles(queryFilter?: QueryFilter) {
    const { data } = await this.mooringService.getBridles(this.mooringId, queryFilter);
    for (const line of data ?? []) {
      line.Components?.sort((a, b) => a.SortIndex - b.SortIndex);
    }

    setTimeout(() => {
      this.bridles = data ?? [];
    }, 10);

    this.review = this.context.getState().offerReview;
  }

  @AsyncErrorHandler
  protected async onSaveBridles({ entries, canNavigateOnSave }: { entries: MooringBridle[]; canNavigateOnSave: boolean }) {
    const dto = entries.map(
      (line) =>
        ({
          Id: line.Id,
          BuoyId: line.BuoyId,
          BuoyNo: line.BuoyNo,
          CageId: line.CageId,
          CageNo: line.CageNo,
          DimForce: line.DimForce,
          MoorlogNo: line.MoorlogNo,
          IsAccident: line.IsAccident,
          AnalysisNo: line.AnalysisNo,

          MblRopes: line.MblRopes,
          MblSinkRopes: line.MblSinkRopes,
          MblStraps: line.MblStraps,
          MblChainShackleConnectionPlates: line.MblChainShackleConnectionPlates,
          MblUsedChainRopeWithKnot: line.MblUsedChainRopeWithKnot,

          CalculatedLength: line.CalculatedLength ? line.CalculatedLength : 0,
          //
          Components: line.Components,
        }) satisfies UpdateMooringBridleDto
    );

    await this.mooringService.updateBridles(this.mooringId, dto);
    this.toaster.showUpdated();
    this.pubsub.publish('list-entity:updated', { name: MooringComponentType.bridle });
    if (canNavigateOnSave) {
      this.pubsub.publish('tabs:change-guard-ok', null);
    } else {
      await this.getBridles();
    }
  }

  protected async setReviewStatus(reviewed: boolean) {
    const confirmed = await this.confirmation.confirmYesCancel(
      'dialog.areYouSure',
      reviewed
        ? this.t.tr('general.markAsCheckedConfirmationText', { x: this.t.tr('mooring.bridle_plural').toLowerCase() })
        : this.t.tr('general.markAsUnCheckedConfirmationText', { x: this.t.tr('mooring.bridle_plural').toLowerCase() })
    );

    if (!confirmed) return;

    await this.mooringOfferService.updateReviewStep(this.reviewId, { Step: 'BRIDLES', Reviewed: reviewed });
    this.toaster.showSuccess(reviewed ? 'mooring.setAsChecked' : 'mooring.setAsNotChecked');
    this.pubsub.publish('entity-parent:updated', null);
  }

  protected unbind() {
    this.pubsub.unsub();
  }

  protected detached() {
    this.pubsub.unsub();
  }
}
